import { useEffect, useState } from 'react';
import {
  apiErrorText,
  useListSmetaContextOptionsQuery,
  useSeedAccumulationMutation,
} from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Field, Input, Label } from '@shared/ui/Input';
import { useAgentPermission } from '@entities/user';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import {
  Err,
  FileHint,
  FormRow,
  FormSelect,
  SubmitWrapper,
  TableTitleZero,
} from './styled';

interface AccumulationSeedFormProps {
  onSeeded: (ledgerId: string) => void;
}

export function AccumulationSeedForm({ onSeeded }: AccumulationSeedFormProps) {
  const [contractNo, setContractNo] = useState('');
  const [seedContractId, setSeedContractId] = useState('');
  const [seedProjectId, setSeedProjectId] = useState('');
  const [objectName, setObjectName] = useState('');
  const [title, setTitle] = useState('');
  const [bookFile, setBookFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { can_write } = useAgentPermission('smeta');
  const { activeProjectId, activeContractId } = useActiveProjectContext();
  const contextOptionsQ = useListSmetaContextOptionsQuery();
  const [seedAccum, { isLoading: seeding }] = useSeedAccumulationMutation();

  const contextContracts = contextOptionsQ.data?.contracts ?? [];
  const contextProjects = contextOptionsQ.data?.projects ?? [];

  useEffect(() => {
    if (!seedProjectId && activeProjectId) {
      setSeedProjectId(activeProjectId);
      const found = contextOptionsQ.data?.projects.find((p) => p.id === activeProjectId);
      if (!objectName.trim() && found?.name) {
        setObjectName(found.name);
      }
    }
    if (!seedContractId && activeContractId) {
      setSeedContractId(activeContractId);
    }
  }, [
    activeProjectId,
    activeContractId,
    contextOptionsQ.data?.projects,
    seedProjectId,
    seedContractId,
    objectName,
  ]);

  async function handleSeed(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!contractNo.trim()) {
      setError('Укажите № договора.');
      return;
    }
    if (!bookFile) {
      setError('Приложите файл книги «Накопительный учет ПА».');
      return;
    }
    const fd = new FormData();
    fd.append('contract_no', contractNo);
    if (seedContractId) fd.append('statut_contract_id', seedContractId);
    if (seedProjectId) fd.append('project_id', seedProjectId);
    if (objectName.trim()) fd.append('object_name', objectName);
    if (title.trim()) fd.append('title', title);
    fd.append('book', bookFile);
    try {
      const res = await seedAccum(fd).unwrap();
      onSeeded(res.ledger_id);
      setContractNo('');
      setSeedContractId('');
      setSeedProjectId('');
      setObjectName('');
      setTitle('');
      setBookFile(null);
    } catch (err) {
      setError(
        apiErrorText(
          err,
          'Не удалось загрузить книгу. Проверьте формат (xlsx с листами «расценки» и «данные ПА»).',
        ),
      );
      // eslint-disable-next-line no-console
      console.error('seedAccumulation failed', err);
    }
  }

  return (
    <Card>
      <form onSubmit={handleSeed}>
        <TableTitleZero>Завести ленту из книги (разовый seed)</TableTitleZero>
        <Field>
          <Label>№ договора</Label>
          <Input
            placeholder="Например: ЗСНХ.17835"
            value={contractNo}
            onChange={(e) => setContractNo(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Привязка к договору Статута и проекту ПТО (необязательно)</Label>
          <FormRow>
            <FormSelect
              value={seedContractId}
              onChange={(e) => {
                const id = e.target.value;
                setSeedContractId(id);
                const found = contextContracts.find((c) => c.id === id);
                if (found?.contract_number && !contractNo.trim()) {
                  setContractNo(found.contract_number);
                }
              }}
            >
              <option value="">— договор Статута —</option>
              {contextContracts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.contract_number ? `№ ${c.contract_number}` : 'договор б/н'} ·{' '}
                  {(c.subject || c.counterparty || '').slice(0, 40)}
                </option>
              ))}
            </FormSelect>
            <FormSelect
              value={seedProjectId}
              onChange={(e) => {
                const id = e.target.value;
                setSeedProjectId(id);
                const found = contextProjects.find((p) => p.id === id);
                if (found?.name && !objectName.trim()) {
                  setObjectName(found.name);
                }
              }}
            >
              <option value="">— проект ПТО / объект —</option>
              {contextProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code ? `${p.code} · ` : ''}
                  {p.name}
                </option>
              ))}
            </FormSelect>
          </FormRow>
        </Field>
        <Field>
          <Label>Объект / стройка (опционально)</Label>
          <Input
            placeholder="Например: Комплекс ГБЖ"
            value={objectName}
            onChange={(e) => setObjectName(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Заголовок ленты (опционально)</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field>
          <Label>Книга «Накопительный учет ПА» (Excel)</Label>
          <Input
            type="file"
            accept=".xlsx,.xlsm"
            onChange={(e) => setBookFile(e.target.files?.[0] ?? null)}
          />
          <FileHint>
            Листы «расценки» + «данные ПА». {bookFile ? bookFile.name : 'файл не выбран'}
          </FileHint>
        </Field>
        {error && <Err>{error}</Err>}
        <SubmitWrapper>
          <Button
            type="submit"
            $variant="primary"
            disabled={seeding || !can_write}
            title={!can_write ? 'Только чтение' : undefined}
          >
            {seeding ? 'Загрузка…' : 'Завести ленту'}
          </Button>
        </SubmitWrapper>
      </form>
    </Card>
  );
}
